import { Typography } from "antd";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import {
  TargetingPreviewResult,
  TargetingPreviewTargetType,
} from "../repository/data.default";
import { getTargetingPreview } from "../repository/data.repository";
import { Subscription, subscription } from "../utils/http";

// A staged target as the caller holds it — the label is whatever display text it staged (used in
// the breakdown; the type name is the fallback).
export interface TargetingPreviewItem {
  readonly type: TargetingPreviewTargetType;
  readonly uid: string;
  readonly label?: string;
}

export interface TargetingPreviewProps {
  /** The staged targets to resolve. Empty = the control renders nothing. */
  readonly targets: TargetingPreviewItem[];
  /** When true, company targets also count their sub-companies' users (materials cascade). */
  readonly includeSubCompanies?: boolean;
  readonly className?: string;
}

const TYPE_FALLBACK: Record<TargetingPreviewTargetType, string> = {
  company: "Company",
  department: "Department",
  user: "User",
};

/**
 * "Scope at a glance" for a staged target set: debounced POST to the TargetingPreview endpoint,
 * rendered as one muted caption line — "Reaches N people" plus a per-company/department breakdown,
 * with out-of-scope targets flagged before the actual submit would 403. Purely informational; it
 * never blocks the caller's flow (a preview failure renders nothing).
 */
function TargetingPreview(props: TargetingPreviewProps) {
  const [result, setResult] = useState<TargetingPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  // The in-flight (possibly delay-pending) preview — superseded requests are cancelled.
  const pending = useRef<Subscription<TargetingPreviewResult>>();

  // The consumers map their staged lists inline (a fresh array identity every render), so the
  // effect keys on the targets' content instead of the array reference.
  const targetsKey = props.targets
    .map((target) => `${target.type}:${target.uid}`)
    .join("|");

  useEffect(() => {
    pending.current?.unsubscribe();
    if (!props.targets.length) {
      pending.current = undefined;
      setResult(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const uidsOf = (type: TargetingPreviewTargetType) =>
      props.targets.filter((t) => t.type === type).map((t) => t.uid);
    const request = subscription(
      () =>
        getTargetingPreview({
          companyUids: uidsOf("company"),
          departmentUids: uidsOf("department"),
          userUids: uidsOf("user"),
          includeSubCompanies: props.includeSubCompanies,
        }),
      400,
    );
    pending.current = request;
    request
      .then((preview) => {
        if (pending.current !== request) return;
        setResult(preview);
        setLoading(false);
      })
      .catch(() => {
        if (pending.current !== request) return;
        setResult(null);
        setLoading(false);
      });

    return () => request.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey, props.includeSubCompanies]);

  if (!props.targets.length) return null;

  const labelOf = (type: TargetingPreviewTargetType, uid: string) =>
    props.targets.find((t) => t.type === type && t.uid === uid)?.label ||
    TYPE_FALLBACK[type];

  // The muted per-target breakdown: company/department targets by name, allowed user targets rolled
  // up into one "+N users" part, out-of-scope targets flagged individually (pre-validation feedback).
  const parts: ReactNode[] = [];
  if (result) {
    result.targets
      .filter((t) => t.allowed && t.type !== "user")
      .forEach((t) =>
        parts.push(
          <Typography.Text
            key={`${t.type}:${t.uid}`}
            type="secondary"
            className="text-xs"
          >
            {labelOf(t.type, t.uid)} · {t.users.toLocaleString()}
          </Typography.Text>,
        ),
      );
    const users = result.targets.filter((t) => t.allowed && t.type === "user");
    if (users.length) {
      parts.push(
        <Typography.Text key="users" type="secondary" className="text-xs">
          +{users.length} {users.length === 1 ? "user" : "users"}
        </Typography.Text>,
      );
    }
    result.targets
      .filter((t) => !t.allowed)
      .forEach((t) =>
        parts.push(
          <Typography.Text
            key={`blocked:${t.type}:${t.uid}`}
            type="warning"
            className="text-xs"
          >
            {labelOf(t.type, t.uid)} — not in your scope
          </Typography.Text>,
        ),
      );
  }

  return (
    <div
      className={`flex min-w-0 flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5 ${
        props.className ?? ""
      }`}
    >
      {loading || !result ? (
        <Typography.Text type="secondary" className="text-xs">
          Resolving reach…
        </Typography.Text>
      ) : (
        <>
          <Typography.Text strong className="text-xs">
            Reaches {result.totalUsers.toLocaleString()}{" "}
            {result.totalUsers === 1 ? "person" : "people"}
          </Typography.Text>
          {parts.map((part, index) => (
            <Fragment key={index}>
              {index > 0 && (
                <Typography.Text type="secondary" className="text-xs">
                  —
                </Typography.Text>
              )}
              {part}
            </Fragment>
          ))}
        </>
      )}
    </div>
  );
}

export default TargetingPreview;
